import React from 'react';
import PropTypes from 'prop-types';
import Analytics from 'analytics-opencare';

import { OptionBlock, Title } from 'pages/common/styled-components';
import { StepWrapper } from 'pages/funnel/styled-components';
import SingleSelect from 'pages/common/components/SingleSelect/SingleSelect';

import { generateStepAnalytics, STEP_KEY } from 'pages/funnel/utils';
import { useFunnelStorage } from 'pages/funnel/storage/Storage';
import { SPECIALTY_FLOW } from 'pages/funnel/specialtyFlow/config';
import Logger from 'utils/Logger';

import { specialistTriagingOptions } from './constants';

const analytics = new Analytics();

const SpecialistTriaging = ({ moveToNextStep }) => {
  const storage = useFunnelStorage();

  const submitAnswer = (selectedOption, index) => {
    analytics.trackClick({
      pageType: analytics.pageTypes.FUNNEL,
      pageName: 'funnel_reason',
      name: 'wizard',
      section: 'specialist-triaging',
      ...generateStepAnalytics(index, storage, {
        response: selectedOption.title,
      }),
    });

    // isUrgent takes precedence over the other options even if all are selected
    const isUrgent = selectedOption.id === 'urgent';
    const isUrgentValue = isUrgent ? 'Yes' : 'No';

    // wasVariant is used to show the variant regardless of the current config
    // This is important since we reset the config if the user doesn't select urgent
    // So if they navigate back they would see the control (since our config is different)
    storage.save(
      STEP_KEY.REASON,
      {
        isUrgent: isUrgentValue,
        triageResponse: selectedOption.id,
        wasVariant: true,
      },
      true,
    );

    if (storage.getActiveConfig()?.id === SPECIALTY_FLOW.CONFIG_ID) {
      const newBranch = {
        'new-dentist': SPECIALTY_FLOW.BRANCHES.NEW_DENTIST,
        specialist: SPECIALTY_FLOW.BRANCHES.SPECIALIST,
        urgent: SPECIALTY_FLOW.BRANCHES.URGENT,
      }[selectedOption.id];

      if (!newBranch) {
        Logger.critical(
          `Specialty Triage: Invalid branch selected: ${selectedOption.id}`,
        );
        return;
      }

      moveToNextStep({ switchBranch: newBranch });
      return;
    }

    // Make sure user has only filled first 3 steps and no more
    const isEmptyAfterReasonStep = storage.isEmpty({
      excludedSteps: [STEP_KEY.WELCOME, STEP_KEY.LOCATION, STEP_KEY.REASON],
    });

    // If user selects non-urgent, reset to get to non-urgent funnel config
    const shouldResetNotUrgent = !isUrgent && isEmptyAfterReasonStep;

    // If the user selects non-urgent and then goes back and changes to urgent,
    // we should reset the funnel in that case as well to get them to the correct non-urgent config
    const savedOption = storage.get(STEP_KEY.REASON);
    const shouldResetChangedToUrgent =
      isUrgent && savedOption?.isUrgent === 'No';

    const shouldReset = shouldResetNotUrgent || shouldResetChangedToUrgent;

    moveToNextStep({ reset: shouldReset });
  };

  return (
    <div>
      <Title id="title">What type of dental care are you looking for?</Title>
      <StepWrapper>
        <form>
          <OptionBlock row={3}>
            {specialistTriagingOptions.map((option, i) => (
              <SingleSelect
                name={option.id}
                key={option.id}
                id={option.id}
                title={option.title}
                tooltip={option.description}
                describedBy="title"
                onChange={() => submitAnswer(option, i)}
              />
            ))}
          </OptionBlock>
        </form>
      </StepWrapper>
    </div>
  );
};

SpecialistTriaging.propTypes = {
  moveToNextStep: PropTypes.func.isRequired,
};

export { SpecialistTriaging };
